import React, { forwardRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { IconButton } from '@components/index';
import InputField, {
  InputFieldProps,
} from '@components/InputField/ui/InputField';
import { Selector } from '@components/Selector/ui/Selector';
import PrevIcon from '@icons/PrevIcon/PrevIcon';

import { range } from '@utils/utils';

import styles from './index.module.scss';

const currentDate = new Date();
const currentYear = currentDate.getFullYear();

interface CustomDatePickerHeaderProps {
  date: Date;
  changeYear: (year: number) => void;
  changeMonth: (month: number) => void;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
}

function CustomDatePickerHeader({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}: CustomDatePickerHeaderProps) {
  const years = range(1901, currentYear);
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return (
    <div className={styles.datePickerHeader}>
      <div className={styles.selectors}>
        <Selector
          showEmptySelect={false}
          values={months}
          value={months[date.getMonth()]}
          onSelect={(month) => changeMonth(months.indexOf(month))}
          selectorClassname={styles.selector}
          optionsClassname={styles.options}
          chevronDownClassname={styles.chevronDownClassname}
          formatValue={(value) => value.substring(0, 3)}
        />
        <Selector
          showEmptySelect={false}
          values={years}
          value={date.getFullYear()}
          onSelect={(year) => changeYear(Number(year))}
          selectorClassname={styles.selector}
          optionsClassname={styles.options}
          chevronDownClassname={styles.chevronDownClassname}
          scrollBottom
        />
      </div>

      <div className={styles.controlWrap}>
        <IconButton
          className={styles.back}
          variant="underline"
          onClick={(e) => {
            e.preventDefault();
            decreaseMonth();
          }}
          disabled={prevMonthButtonDisabled}
        >
          <PrevIcon stroke="#98A2B3" />
        </IconButton>
        <IconButton
          className={styles.next}
          variant="underline"
          onClick={(e) => {
            e.preventDefault();
            increaseMonth();
          }}
          disabled={nextMonthButtonDisabled}
        >
          {/* Rotado 180° con CSS para mostrar la flecha al otro lado */}
          <PrevIcon stroke="#98A2B3" />
        </IconButton>
      </div>
    </div>
  );
}

interface PrefixInputFieldProps extends InputFieldProps {
  prefix?: string;
}

const PrefixInputField = forwardRef<HTMLInputElement, PrefixInputFieldProps>(
  ({ prefix, placeholder, ...props }, ref) => {
    return (
      <div
        style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
      >
        {prefix && <span style={{ marginRight: '6px' }}>{prefix}</span>}
        <InputField
          ref={ref}
          {...props}
          placeholder={placeholder}
          className={styles.inputWidth}
        />
      </div>
    );
  }
);

interface DatePickerProps {
  className?: string;
  selected?: Date | null;
  onChange?: (date: Date | null) => void;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  customInput?: React.ReactNode;
  prefix?: string;
}

function CustomDatePicker({
  className = '',
  selected,
  onChange,
  placeholder,
  minDate,
  maxDate,
  customInput,
  prefix,
}: DatePickerProps) {
  const [currentMonth, setCurrentMonth] = useState<number>(
    selected?.getMonth() ?? new Date().getMonth()
  );

  return (
    <div className={[styles.datePicker, className].join(' ')}>
      <DatePicker
        placeholderText="MM/DD/YYYY"
        selected={selected}
        onChange={(date) => onChange && onChange(date)}
        minDate={minDate}
        maxDate={maxDate}
        renderCustomHeader={(headerProps) => (
          <CustomDatePickerHeader {...headerProps} />
        )}
        dayClassName={(date) =>
          date.getMonth() === currentMonth
            ? styles.datePickerDay
            : styles.disabledPickerDay
        }
        dateFormat="MM/dd/yyyy"
        formatWeekDay={(day) => day.substring(0, 3)}
        onMonthChange={(date: Date) => setCurrentMonth(date.getMonth())}
        customInput={
          customInput || (
            <PrefixInputField
              prefix={prefix}
              placeholder={placeholder || 'Select a date'}
            />
          )
        }
      />
    </div>
  );
}

export default CustomDatePicker;
